import { useEffect, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PartnerInfoPage.css";

// 학원 관리 > 협력학원 관리. 협력학원이 자기 학원 정보를 직접 적는다(2026-09-28).
// 지인에듀 홈페이지의 학원 찾기 지도와 학원 소개 페이지가 이 값을 쓴다.
// 관리자가 계정을 이어 둔 협력학원만 쓸 수 있다(서버 partner_info_views.py).
// 담당 학교와 교과서는 학원 관리 > 프로모션으로 옮겼다(2026-09-30). 줄마다 자료 링크가 된다.

const BASIC_FIELD_LIST = [
  { key: "english_name", label: "학원 영어 이름", placeholder: "예: JIIN ACADEMY" },
  { key: "address", label: "주소", placeholder: "도로명 주소", wide: true },
  { key: "dong", label: "동네 이름", placeholder: "예: 장대동" },
  { key: "phone", label: "학원 전화번호", placeholder: "042-000-0000" },
  { key: "email", label: "이메일", placeholder: "레벨테스트 신청 알림을 받을 주소" },
  { key: "blog", label: "블로그 주소", placeholder: "https://blog.naver.com/..." },
  { key: "cafe", label: "카페 주소", placeholder: "https://cafe.naver.com/..." },
  { key: "insta", label: "인스타그램", placeholder: "https://instagram.com/... 또는 @아이디" },
  { key: "homepage", label: "홈페이지", placeholder: "https://..." },
  { key: "applicant_name", label: "원장님 성함", placeholder: "소개 페이지에 나갑니다" },
  { key: "applicant_phone", label: "담당자 연락처 (공개 안 함)", placeholder: "010-0000-0000" },
];

const PHOTO_LIST = [
  { kind: "director", field: "director_photo_url", label: "원장 사진", help: "상반신, 밝은 배경, 가능한 한 고화질로 올려 주세요." },
  { kind: "space", field: "space_photo_url", label: "학원 사진", help: "교실이나 로비처럼 학원이 잘 보이는 가로 사진이 좋아요." },
];

const PhotoBox = ({ photo, url, onUploaded }) => {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    setIsUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("kind", photo.kind);
    formData.append("file", file);
    try {
      const response = await apiClient.post("/api/v1/echo/teacher/partner/photo", formData, {
        withAuth: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(response.data.data);
    } catch (caught) {
      setError(caught.response?.data?.error || "사진을 올리지 못했어요. 다시 시도해 주세요.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="partner-info__photo-bar">
      <span className="partner-info__photo-title">{photo.label}</span>
      {url ? <img className="partner-info__photo-thumb" src={url} alt={photo.label} /> : <span className="partner-info__photo-thumb partner-info__photo-thumb--empty" />}
      <span className="partner-info__photo-status">
        {isUploading ? "올리는 중..." : url ? "올렸어요" : photo.help}
        {error && <span className="partner-info__error"> {error}</span>}
      </span>
      <button type="button" className="partner-info__photo-button" disabled={isUploading} onClick={() => inputRef.current.click()}>
        {url ? "바꾸기" : "사진 올리기"}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => upload(event.target.files[0])}
      />
    </div>
  );
};

const PartnerInfoPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPartner, setIsPartner] = useState(false);
  const [form, setForm] = useState({});
  const [answerMap, setAnswerMap] = useState({});
  const [photoUrlMap, setPhotoUrlMap] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  const applyData = (data) => {
    setForm(
      BASIC_FIELD_LIST.reduce((acc, field) => ({ ...acc, [field.key]: data[field.key] || "" }), { tagline: data.tagline || "" })
    );
    setAnswerMap(data.answer_map || {});
    setPhotoUrlMap({ director_photo_url: data.director_photo_url, space_photo_url: data.space_photo_url, name: data.name });
  };

  useEffect(() => {
    apiClient
      .get("/api/v1/echo/teacher/partner", { withAuth: true })
      .then((response) => {
        if (response.data.found) {
          setIsPartner(true);
          applyData(response.data.data);
        }
      })
      .finally(() => setIsLoaded(true));
  }, []);

  const save = async () => {
    setIsSaving(true);
    setMessage("");
    try {
      await apiClient.put(
        "/api/v1/echo/teacher/partner",
        { ...form, answer_map: answerMap },
        { withAuth: true }
      );
      setMessage("저장했어요. 지인에듀 홈페이지 학원 소개에 반영됩니다.");
    } catch (caught) {
      setMessage(caught.response?.data?.error || "저장하지 못했어요. 다시 시도해 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isLoaded) return null;
  if (!isPartner) {
    return (
      <div className="partner-info">
        <div className="partner-info__card partner-info__empty">
          협력학원으로 등록된 학원만 쓸 수 있어요. 협력학원 신청은 지인에듀로 문의해 주세요.
        </div>
      </div>
    );
  }

  return (
    <div className="partner-info">
      <div className="partner-info__head">
        <div className="section-title">협력학원 관리</div>
        <p className="partner-info__lead">
          적어 주신 내용으로 지인에듀 홈페이지의 <b>학원 찾기</b>와 <b>{photoUrlMap.name || "학원"} 소개 페이지</b>를 만듭니다.
        </p>
      </div>

      <section className="partner-info__card">
        <div className="partner-info__card-title">학원 정보</div>
        <div className="partner-info__grid">
          {BASIC_FIELD_LIST.map((field) => (
            <label key={field.key} className={`partner-info__field${field.wide ? " partner-info__field--wide" : ""}`}>
              <span>{field.label}</span>
              <input
                value={form[field.key] || ""}
                placeholder={field.placeholder}
                onChange={(event) => setForm((prev) => ({ ...prev, [field.key]: event.target.value }))}
              />
            </label>
          ))}
        </div>
      </section>

      <section className="partner-info__card">
        <div className="partner-info__card-title">사진</div>
        <div className="partner-info__photo-row">
          {PHOTO_LIST.map((photo) => (
            <PhotoBox key={photo.kind} photo={photo} url={photoUrlMap[photo.field]} onUploaded={applyData} />
          ))}
        </div>
      </section>

      <section className="partner-info__card">
        <div className="partner-info__card-title">학원 소개</div>
        <label className="partner-info__field partner-info__field--wide">
          <span>한줄 소개</span>
          <input
            value={form.tagline || ""}
            placeholder="학원 찾기 카드와 소개 페이지 첫 화면에 나가는 한 문장"
            onChange={(event) => setForm((prev) => ({ ...prev, tagline: event.target.value }))}
          />
        </label>
        <label className="partner-info__field partner-info__field--wide partner-info__philosophy">
          <span>교육철학 <em>{(answerMap.philosophy || "").length.toLocaleString()} / 10,000자</em></span>
          <textarea
            rows={3}
            maxLength={10000}
            placeholder="써 두신 글을 그대로 붙여 넣으셔도 됩니다."
            value={answerMap.philosophy || ""}
            onChange={(event) => setAnswerMap((prev) => ({ ...prev, philosophy: event.target.value }))}
          />
        </label>
      </section>

      <div className="partner-info__save">
        {message && <span className="partner-info__message">{message}</span>}
        <button type="button" className="partner-info__save-button" disabled={isSaving} onClick={save}>
          {isSaving ? "저장 중..." : "저장하기"}
        </button>
      </div>
    </div>
  );
};

export default PartnerInfoPage;
